import { t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, Fragment, type ReactNode } from "react";
import { Button } from "antd";
import { Navigate, useLocation } from "react-router-dom";
import { ActiveShowroomSessionProvider } from "../../../features/showroom/ActiveShowroomSession.tsx";
import LoadingRing from "../../shared/LoadingRing.jsx";
import AppFeedback from "../../shared/AppFeedback.tsx";
import { agencyBootstrap, agencyContext } from "./agencyIdentityService.ts";
import { agencySessionKey, setAgencySession, subscribeAgencySession } from "../../../utils/agencySession.ts";
import { isRajMarketingHost } from "../../../features/raj-landing/rajMarketingDomain.ts";

export default function AgencySessionBoundary({ children }: { children: ReactNode }) {
  useStoreLanguage();
  const { pathname } = useLocation();
  const route = pathname.replace(/\/+$/, "").toLowerCase() || "/";
  // Public brand/authentication pages need no agency session; admin verifies its own.
  // The retired /raj route renders NotFound without redirecting to sign-in.
  const skip = ["/raj", "/login", "/register", "/invitation", "/agency/login", "/customer/login", "/customer/register"].includes(route)
    || isRajMarketingHost() || route === "/admin" || route.startsWith("/admin/");
  if (skip) return <>{children}</>;
  return (
    <HydratedAgencySession>
      {children}
    </HydratedAgencySession>
  );
}

function HydratedAgencySession({ children }: { children: ReactNode }) {
  useStoreLanguage();
  const sessionKey = useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  const pending = useRef(false);
  const checkedAt = useRef(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async function (background = false) {
    if (pending.current) return;
    if (background && Date.now() - checkedAt.current < 60_000) return;
    pending.current = true;
    const requestedSession = agencySessionKey();
    if (!background) setLoading(true);
    setFailed(false);
    try {
      const bootstrap = await agencyBootstrap();
      checkedAt.current = Date.now();
      if (requestedSession !== agencySessionKey()) return;
      if (bootstrap.isAuthenticated) await agencyContext();
      else setAgencySession(null);
    } catch (error) {
      if (requestedSession !== agencySessionKey()) return;
      setAgencySession(null);
      setFailed(true);
      AppFeedback.httpError(error);
    } finally { pending.current = false; setLoading(false); }
  }, []);
  useEffect(function () {
    void load();
    function refreshVisible() {
      if (document.visibilityState === "visible") void load(true);
    }
    window.addEventListener("focus", refreshVisible);
    document.addEventListener("visibilitychange", refreshVisible);
    return function () {
      window.removeEventListener("focus", refreshVisible);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [load]);
  if (loading) return <LoadingRing tip={t("در حال بررسی…")} />;
  if (!sessionKey) {
    return <Navigate to="/login" replace />;
  }
  if (failed) {
    return (
      <Button
        loading={loading}
        disabled={loading}
        onClick={function () { void load(); }}
      >{t("تلاش دوباره برای بررسی نشست")} </Button>
    );
  }
  return (
    <Fragment key={sessionKey}>
      <ActiveShowroomSessionProvider>
        {children}
      </ActiveShowroomSessionProvider>
    </Fragment>
  );
}
